import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BannerSection from "../../components/common/BannerSection";
import { Box, Button, useTheme, useMediaQuery, Typography } from "@mui/material";
import CampaignCard from "../../components/common/CampaignCard/CampaignCard";
import CampaignCardSkeleton from "../../components/common/CampaignCard/CampaignCardSkeleton";
import { getCampaigns } from "../../api/services/campaignService";
import { canViewPrivateCampaign } from "../../utils/authUtils";

function SearchResults() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isDesktop = useMediaQuery("(min-width: 1390px)");

  // Fetch campaigns from API (no keyword filter - only API data)
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const response = await getCampaigns();
        if (response && response.data && Array.isArray(response.data)) {
          setCampaigns(response.data);
        } else {
          setCampaigns([]);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching campaigns:", err);
        setError(err);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  // Filter: privacy → isSearch === 1 only (if any) → sort by latest updated → max 6. Fallback: if none have isSearch=1, show latest 6.
  const searchResults = useMemo(() => {
    const privacyFiltered = campaigns.filter((c) => {
      if (c.IsPrivate === 0 || !c.IsPrivate) return true;
      if (c.IsPrivate === 1) return canViewPrivateCampaign(user, isAuthenticated);
      return true;
    });

    const searchable = privacyFiltered.filter((c) => {
      const flag = c.isSearch ?? c.is_search ?? 0;
      return flag === 1 || flag === "1" || String(flag) === "1";
    });

    const toSort = searchable.length > 0 ? searchable : privacyFiltered;
    const sorted = toSort.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.updated_at || a.createdAt || a.created_at || 0);
      const dateB = new Date(b.updatedAt || b.updated_at || b.createdAt || b.created_at || 0);
      if (dateB.getTime() !== dateA.getTime()) return dateB.getTime() - dateA.getTime();
      return (b.id || 0) - (a.id || 0);
    });

    return sorted.slice(0, 6);
  }, [campaigns, user, isAuthenticated]);

  const handleExploreAll = () => {
    navigate("/campaigns");
  };

  return (
    <>
      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection
          title="Search Results"
          subtitle="Campaigns you can support"
          backgroundImage={null}
        />
      </Box>

      <Box
        sx={{
          width: isMobile ? "100%" : "94.44%",
          maxWidth: "1440px",
          borderRadius: isMobile ? "16px" : "36px",
          backgroundColor: "rgba(232, 232, 232, 0.36)",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-evenly",
          alignItems: "center",
          marginTop: isMobile ? "20px" : "40px",
          marginBottom: "40px",
          padding: isMobile ? "26px 0px 20px 0px" : "60px 0px 40px 0px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Campaign Cards Section - same layout as Featured Campaigns: grid on desktop, horizontal scroll on smaller */}
        <Box
          sx={{
            display: isDesktop ? "grid" : "flex",
            gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "none",
            gridTemplateRows: isDesktop ? "repeat(2, auto)" : "none",
            width: "100%",
            justifyContent: isDesktop ? "center" : "flex-start",
            alignItems: "center",
            gap: isMobile ? "16px" : isTablet ? "20px" : "24px",
            marginBottom: isMobile ? "20px" : "48px",
            paddingLeft: isMobile ? "16px" : "34px",
            paddingRight: isMobile ? "16px" : "34px",
            overflowX: isDesktop ? "hidden" : "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
          className="hide-scrollbar"
        >
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <CampaignCardSkeleton key={`search-skeleton-${index}`} />
            ))
          ) : searchResults.length > 0 ? (
            searchResults.map((campaign) => (
              <Box
                key={campaign.id}
                sx={{
                  flexShrink: 0,
                  ...(isMobile
                    ? {
                        width: "290px",
                        minWidth: "290px",
                        maxWidth: "290px",
                      }
                    : {}),
                }}
              >
                <CampaignCard campaignData={campaign} isCampaign={!isMobile} />
              </Box>
            ))
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                padding: "40px 20px",
                color: "#666666",
                fontSize: "16px",
                fontWeight: 500,
                textAlign: "center",
                gridColumn: isDesktop ? "1 / -1" : "none",
              }}
            >
              <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, color: "#686866" }}>
                No searchable campaigns at the moment.
              </Typography>
            </Box>
          )}
        </Box>

        <Button
          onClick={handleExploreAll}
          sx={{
            backgroundColor: "#1D1D1B",
            color: "#FFFFFF",
            borderRadius: "8px",
            padding: "14px 32px",
            fontFamily: "Red Hat Display",
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "14px",
            textTransform: "none",
            "&:hover": { backgroundColor: "#2D2D2B" },
          }}
        >
          Explore All Campaigns
        </Button>
      </Box>
    </>
  );
}

export default SearchResults;
